import React from 'react';

import { GoalStatusesLabel } from 'Shared/constants/types';

import Colors from 'Wpds/colors/ColorConstants';
import { unixDateMMMDDYYYY } from 'Shared/utils/DateTime.utils';

import WpIcon from 'Wpds/components/icon/WpIcon';

import './HistoryItem.scss';

type Props = {
  history: {
    userName: string;
    goalName: string;
    assigneeName: string;
    userId: number;
    timestamp: number;
    changeType: string;
    before: string | number;
    after: string | number;
  };
};

const getDetails = ({ history }: Props) => {
  const name = history.userName;
  const goalName = history.goalName || 'Goal';
  const assigneeName = history.assigneeName || 'Assignee';

  const timestamp = <span className="HistoryItem__item__label__time">{unixDateMMMDDYYYY(history.timestamp)}</span>;

  const getGoalStatus = status => GoalStatusesLabel[status];

  switch (history.changeType) {
    case 'created':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> created the goal - {timestamp}
          </div>
        </div>
      );
    case 'changed_status':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> changed the status - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__before">{getGoalStatus(history.before)}</span>
            <span className="HistoryItem__item__value__arrow">
              <WpIcon name="arrow-right" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{getGoalStatus(history.after)}</span>
          </div>
        </div>
      );
    case 'changed_complete':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> marked the goal{' '}
            {history.after ? 'complete' : 'incomplete'} - {timestamp}
          </div>
        </div>
      );
    case 'added_sub_goal':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> linked a sub-goal - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__icon">
              <WpIcon name="bullseye-arrow" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{goalName}</span>
          </div>
        </div>
      );
    case 'removed_sub_goal':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> unlinked a sub-goal - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__icon">
              <WpIcon name="bullseye-arrow" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{goalName}</span>
          </div>
        </div>
      );
    case 'changed_assignee_id':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> changed the owner to {assigneeName} -{' '}
            {timestamp}
          </div>
        </div>
      );
    case 'changed_parent_goal':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> {history.after ? 'linked' : 'unlinked'} a
            parent goal - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__icon">
              <WpIcon name="bullseye-arrow" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{goalName}</span>
          </div>
        </div>
      );
    case 'changed_metric_current_value':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> updated progress value - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__before">{history.before}</span>
            <span className="HistoryItem__item__value__arrow">
              <WpIcon name="arrow-right" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{history.after}</span>
          </div>
        </div>
      );
    case 'changed_metric_goal_value':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> updated progress target - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__before">{history.before}</span>
            <span className="HistoryItem__item__value__arrow">
              <WpIcon name="arrow-right" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{history.after}</span>
          </div>
        </div>
      );
    case 'changed_metric_initial_value':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> updated progress start value - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__before">{history.before}</span>
            <span className="HistoryItem__item__value__arrow">
              <WpIcon name="arrow-right" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{history.after}</span>
          </div>
        </div>
      );
    case 'changed_metric_type':
      return (
        <div className="HistoryItem__item">
          <div className="HistoryItem__item__label">
            <span className="HistoryItem__item__label__bold">{name}</span> updated goal progress type - {timestamp}
          </div>
          <div className="HistoryItem__item__value">
            <span className="HistoryItem__item__value__before">{history.before}</span>
            <span className="HistoryItem__item__value__arrow">
              <WpIcon name="arrow-right" color={Colors.gray[700]} />
            </span>
            <span className="HistoryItem__item__value__after">{history.after}</span>
          </div>
        </div>
      );
    default:
      return false;
  }
};

const HistoryItem = ({ history }: Props) => {
  return <div className="HistoryItem">{getDetails({ history })}</div>;
};

export default HistoryItem;
